import { DynaStatus } from './dac.types';
import { DocType, passingModeStep, passingModeType } from './dtos.types';
import { DynaDocsModel } from './dyna-docs-model.types';
import { ClarificationDocument } from './clarification-request.types';

export interface BankDetails {
  id: number;
  nom: string;
  agence: string;
  rib: string;
  pays: RefParams;
  local: boolean;
  societe: Company;
}

export interface Address {
  id: number;
  email: string;
  tel1: string;
  tel2: string;
  fax: string;
  adresse: string;
  compAdresse: string;
  codePostal: string;
  ville: string;
  pays: RefParams;
}

export interface Contact {
  id: number;
  nom: string;
  address: string;
  tel1: string;
  tel2: string;
  email: string;
}

export interface CompanyContact {
  contact: Contact;
  societeContact: {
    principal: boolean;
    representantLegal: boolean;
  };
}

export interface Ac {
  id: number;
  nom: string;
  abreviation?: string;
  nomAr?: string;
  ancrage?: string;
  contact?: Contact;
  email?: string;
  logo?: string;
  istTutel?: boolean;
  typeAffCommission?: string | null;
  coms?: Committee[];
}

export interface AcYearAvailability {
  acId: number;
  acName: string;
  allowedYears: number[];
}

export interface AcHistory {
  nom: string;
  prenom: string;
  /*
  statut: string;
*/
  dateDebut: Date;
  dateFin: Date;
}

export interface SettingsAc {
  id: number;
  nom: string;
  abreviation?: string;
  nomAr?: string;
  tutel: Ac;
  ancrage: string;
  contact: string;
  users: User[];
  com?: { id: number; nom: string };
  email?: string;
  logo?: string;
  istTutel?: boolean;
  typeAffCommission?: string | null;
  adresse?: {
    id?: number;
    tel1?: string;
    tel2?: string;
    fax?: string;
    adresse?: string;
    compAdresse?: string;
    codePostal?: string;
    ville?: string;
    paysId?: number;
    pays?: { description: string };
  };
}

export interface Committee {
  id: number;
  nom: string;
  abreviation?: string;
  nomAr?: string;
  decretCreation: string;
  responsableId: string;
  special: boolean;
  ancrage: string;
  contact: string;
  adresse: string;
  users: User[];
  tutel?: Ac;
  comType: RefParams;
  conselliers?: User[];
}

export interface ConsellierComHistoriqueResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  dateDebut: string;
  dateFin: string | null;
}

export interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  tel: string;
  adresse: string;
  indication?: string;
  createdAt?: string;
  createdBy?: number;
}

export interface Company {
  id: number;
  nom: string;
  nif: string;
  email: string;
  responsablePrenom: string;
  responsableNom: string;
  responsableEmail: string;
  pays: string;
}
export interface CompanyDetails {
  id: number;
  nom: string;
  type: RefParams;
  categorieSoumissionnaire: RefParams;
  nif: string;
  dateNif: Date;
  numCnss: string;
  status: boolean;
  address: Address;
  activites?: Activity[];
  usersSociete?: CompanyUser[];
  societeDocs?: {
    id: number;
    rcDoc?: ClarificationDocument;
    nifDoc?: ClarificationDocument;
    cinDoc?: ClarificationDocument;
  };
}

export interface CompanyUser {
  userId: number;
  societeId: number;
  responsable: boolean;
  nom: string;
  prenom: string;
  numCin: string;
  deteDelivranceCin: Date;
  tel1: string;
  tel2: string;
  email: string;
  status: boolean;
}

export interface RefType {
  id: number;
  description: string;
}

export interface RefParams {
  id: number;
  code: string;
  description: string;
  refType?: RefType;
  codeColor?: string;
  systemType?: boolean;
}

export enum RefTypeEnum {
  COMPANY_TYPE = 'type-societe',
  COMMITEE_TYPE = 'type-comite',
  ADDRESS_TYPE = 'type-address',
  STATUS = 'statut',
  COUNTRY = 'pays',
  TUTEL = 'tutel',
  ACTIVITY_TYPE = 'categorie-activite',
  //milestone
  MARKET_TYPE = 'type-marcher',
  FINANCIAL_SOURCE = 'source-financement',
  CURRENCY = 'monais',
  SELECTION_MODE = 'mode-selection',
  BUDGET_ALLOCATION = 'Imputation_budgetaire',
  APPROVAL_ISSUING_BODY = 'organisme-de-delivrance-agrements',

  //dac
  DYNA_FIELD = 'type-dyna-field',
  DAC_CATEGORY = 'dac-category',
}

export interface Direction {
  id: number;
  libelle: string;
}

export interface Currency {
  id: number;
  code: string;
  description: string;
  taux: number;
}

export interface CurrencyHistory {
  id: number;
  deviseId: number;
  code: string;
  description: string;
  taux: number;
  dateDebut: Date;
  dateFin: Date;
  action: string;
  changedBy: string;
}

export interface CommitteeHistory {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  dateDebut: Date;
  dateFin: Date;
}

export interface PassingMode {
  id: number;
  libelle: string;
  type: passingModeType | null;
  etapes: passingModeStep | null;
  restraint: boolean;
  typeMarchers?: RefParams[];
}

export interface DelaisReglementaires {
  id: {
    typeMarcher: RefParams;
    modePassation: PassingMode;
  };
  delaiPublicationLancement: number;
  delaiLancementAttribution: number;
  delaiAttributionDemarrage: number;
}

export interface Activity {
  id: number;
  libelle: string;
  categorieActivite?: RefParams;
}

export interface DocSubmission {
  id: number;
  nom: string;
  typeDossier?: DocType;
  validationDateRequired: boolean;
}

export interface DocSubmissionDto {
  nom: string;
  validationDateRequired: boolean;
  typeDossierId?: number;
}

export interface ApprovalType {
  id: number;
  libelle: string;
  organismeDeDeliveranceAgrements?: RefParams;
}

//Dynamic Forms *********************************************************************

export enum FormType {
  FORM = 'FORM',
  TABLE = 'TABLE',
}

export interface DynamicForm {
  id: number;
  version: number;
  name: string;
  order: number;
  typeDossier: DocType;
  typeDossierId: number;
  formType: FormType;
  status: DynaStatus;
  fields: DynamicField[];
}

export interface DynamicFormDto {
  id: number;
  name: string;
  order?: number;
  typeDossierId?: number;
  formType?: FormType;
  status: DynaStatus;
  fields: DynamicFieldDto[];
}

export interface DynamicField {
  dynaFormFieldsId: number;
  status: DynaStatus;
  name: string;
  label: string;
  description: string;
  type: DynamicFieldType;
  isRequired: boolean;
  order: number;
  hasTotal: boolean;
  isFormula: boolean;
  formula: string;
}

export interface DynamicFieldDto {
  dynaFormFieldsId: number | null;
  status: DynaStatus;
  name: string;
  label: string;
  description: string;
  type?: DynamicFieldType;
  isRequired: boolean;
  order?: number;
  hasTotal: boolean;
  isFormula: boolean;
  formula: string;
}

export enum DynamicFieldType {
  TEXT = 'TEXT',
  INTEGER = 'INTEGER',
  DECIMAL = 'DECIMAL',
  DATE = 'DATE',
  // LIST = 'LIST',
  // TABLE = 'TABLE',
}

//********************************************************************************* */

export enum DecisionEnum {
  TEMPORAIRE = 'TEMPORAIRE',
  PERMANENT = 'DEFINITIVE',
}

export interface CompanyList {
  id: number;
  nom: string;
  nif: string;
  pays: string;
}

export interface CompanyBlackListDto {
  societeId: number;
  natureDecision: DecisionEnum;
  dateEffet: Date;
  delai?: Date;
}

export interface CompanyBlackList {
  id: number;
  societeId: number;
  nom: string;
  nif: string;
  pays: string;
  natureDecision: DecisionEnum;
  dateEffet: string;
  delai: string;
}

export interface SocieteDacListItem {
  societe: {
    id: number;
    nom: string;
    type: RefParams;
    categorieSoumissionnaire: RefParams;
    nif: string;
    dateNif: string;
    numCnss: string;
    status: string;
  };
  dacId: number;
  dacVer: number;
  dacReference?: string | null;
  dateLimiteSoumission?: string | null;
  methodePaiement: string;
  statusPaiement: string | null;
  justifPaiementId: number;
}

export interface FundingSource {
  id: number;
  code: string;
  description: string;
  bailleurDeFonds: boolean;
}

export interface FundingSourceDto {
  code: string;
  description: string;
  bailleurDeFonds: boolean;
}

export interface ParametrageGeneralDac {
  id: number;
  dynaDocsModels: { dynaDocsModel: DynaDocsModel; paramGeneralStatus: ParamGeneralStatus }[];
  dynaDocsModels_joined?: string;
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralSource?: string;
  paramGeneralSourceField?: string;
  paramGeneralOrder?: number;
  paramGeneralStatus?: ParamGeneralStatus;
}

export interface ParametrageGeneralDacDto {
  dynaDocsModelIds?: number[];
  dynaDocsModels?: { dynaDocsModelId: number; paramGeneralStatus: string }[];
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralOrder?: number;
  paramGeneralSource?: string;
  paramGeneralSourceField?: string;
  paramGeneralStatus?: ParamGeneralStatus;
}

export interface ParamGeneralDacBatchDto {
  id?: number;
  dynaDocsModels?: { dynaDocsModelId: number; paramGeneralStatus: string }[];
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralSource?: string;
  paramGeneralSourceField?: string;
  paramGeneralOrder?: number;
  paramGeneralStatus?: ParamGeneralStatus;
}

export enum ParamGeneralType {
  INTEGER = 'INTEGER',
  TEXT = 'TEXT',
  DECIMAL = 'DECIMAL',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  SYSTEM = 'SYSTEM',
}

export enum ParamGeneralStatus {
  ACTIF = 'ACTIF',
  NON_ACTIF = 'NON_ACTIF',
}

export interface ParamGeneralField {
  id?: number;
  paramGeneralName: string;
  paramGeneralLabel: string;
  paramGeneralDescription: string;
  paramGeneralType: ParamGeneralType;
  paramGeneralOrder: number;
  paramGeneralSource: string;
  paramGeneralSourceField: string;
  paramGeneralStatus: ParamGeneralStatus;
}

export interface ParamGeneralFieldDto {
  id?: number;
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralOrder?: number;
  paramGeneralSource: string;
  paramGeneralSourceField: string;
  paramGeneralStatus?: ParamGeneralStatus;
}

export interface ParamGeneralDacValue {
  id: number;
  paramGeneralDac: number;
  dacId: number;
  dacVer: number;
  typeMarche: number;
  modePassation: number;
  dynaDocsModel: number;
  paramGeneralValue: string;
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralOrder?: number;
  paramGeneralStatus?: ParamGeneralStatus;
}

export interface ParamGeneralDacValueDto {
  id: number;
  paramGeneralDac: number;
  dacId: number;
  dacVer: number;
  paramGeneralValue: string;
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  paramGeneralDescription?: string;
  paramGeneralType?: ParamGeneralType;
  paramGeneralOrder?: number;
  paramGeneralStatus?: ParamGeneralStatus;
}

// A global (reusable) variable as placed inside one specific document dynamique (section).
// This is what the section's field-list screen and the end-user fill-in form render -
// local fields (DynaFields) and these placements are shown together, sorted by `ordre`.
export interface ParamGeneralDacPlacement {
  placementId: number;
  paramGeneralDacId: number;
  paramGeneralName?: string;
  paramGeneralLabel?: string;
  // Effective description shown wherever this placement is used - this placement's own
  // override when set, otherwise the base variable's own description.
  paramGeneralDescription?: string;
  // Raw override only (undefined/null when this placement just uses the base description) -
  // use this, not paramGeneralDescription, to pre-fill an edit box for the override itself.
  placementDescription?: string | null;
  paramGeneralType?: ParamGeneralType;
  paramGeneralSource?: string;
  paramGeneralSourceField?: string;
  dynaDocsId: number;
  dynaDocsVer: number;
  ordre?: number;
  paramGeneralStatus?: ParamGeneralStatus;
  // True when this is the placement with the lowest document order among all sections (of the
  // same document type) that share this variable - i.e. the one section allowed to edit it.
  // Always true for a variable placed in only one section.
  isFirstSection?: boolean;
}

export interface ParamGeneralDacPlacementRequest {
  paramGeneralDacId: number;
  dynaDocsId: number;
  dynaDocsVer: number;
  ordre?: number;
  description?: string;
}

// Overrides (or, sent blank/undefined, clears) one placement's description - independent of
// the base variable's own description and of any other placement of the same variable.
export interface ParamGeneralDacPlacementUpdateRequest {
  description?: string;
}

export interface ParamGeneralDacPlacementReorderItem {
  placementId: number;
  ordre: number;
}

// One section's field list, local + shared, as a single ordered sequence - itemId is the
// DynaFields' dynaFieldsId for FIELD items, the placement's own id for PLACEMENT items.
export enum ParamGeneralDacMixedItemType {
  FIELD = 'FIELD',
  PLACEMENT = 'PLACEMENT',
}

export interface ParamGeneralDacMixedReorderItem {
  itemType: ParamGeneralDacMixedItemType;
  itemId: number;
  order: number;
}
